import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ContentTitle } from '../../ui/styled';
import { LeftGrid } from '../../Episode/Episode.Styled';
import BackIcon from '../../common/Back/BackIcon';
import PageHeading from '../../common/Heading/PageHeading';
import ProgressBar from '../../common/ProgressBar/ProgressBar';
import { CREDITED_TREATMENTS_URL } from '../../../constants';
import { getStopApi } from '../../../services/StopApi/StopApi';
import CreditedTreatmentsPrintDialog from './CreditedTreatmentsPrintDialog';

const CreditedTreatmentsList = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [treatmentsData, setTreatmentsData] = useState([]);
  const [groupedTreatments, setGroupedTreatments] = useState({});
  const [openPrintDialog, setOpenPrintDialog] = useState(false);

  const invoiceReferenceData = useSelector(
    (state) => state.invoiceReferenceData?.data
  );
  const { selectedMonth } = invoiceReferenceData;

  useEffect(() => {
    getUser().then((userData) => setUser(userData));
  }, []);

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken && selectedMonth) {
      fetchData();
    }
  }, [user?.signInUserSession.accessToken.jwtToken, selectedMonth]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const fetchData = async () => {
    setLoading(true);
    const commonParams = `${CREDITED_TREATMENTS_URL}${selectedMonth}`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;

    try {
      const response = await getStopApi({ commonParams, authorization });
      const data = response.data.invoiceDetails || [];

      // Group by mthID, then by CBP Name
      const grouped = data.reduce(function (r, a) {
        r[a.mthID] = r[a.mthID] || {};
        r[a.mthID][a.cboName] = r[a.mthID][a.cboName] || [];
        r[a.mthID][a.cboName].push(a);
        return r;
      }, Object.create(null));

      setTreatmentsData(data);
      setGroupedTreatments(grouped);
      setLoading(false);
      // Auto-open print dialog only if data is found
      if (data.length > 0) {
        setOpenPrintDialog(true);
      }
    } catch (error) {
      console.error('Error fetching credited treatments:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => {
    setOpenPrintDialog(true);
  };

  return (
    <>
      <Box>
        <ContentTitle container>
          <Grid item xs={10}>
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <BackIcon />
              <PageHeading title='Credited Treatments Report' />
            </Box>
          </Grid>
          <LeftGrid item xs={2}>
            <Button
              variant="contained"
              style={{ justifyContent: 'flex-end' }}
              onClick={handlePrint}
              disabled={loading || treatmentsData.length === 0}
            >
              Print Preview
            </Button>
          </LeftGrid>
        </ContentTitle>

        {loading ? (
          <ProgressBar />
        ) : treatmentsData.length === 0 ? (
          <Box p={2}>
            <Typography variant="h6">
              No Credited Treatments Found for Invoice Month {selectedMonth}
            </Typography>
          </Box>
        ) : (
          <Box p={2}>
            <p>Found {treatmentsData.length} credited treatments across {Object.keys(groupedTreatments).length} different months.</p>
          </Box>
        )}

        {treatmentsData.length > 0 && (
          <CreditedTreatmentsPrintDialog
            invoiceMonth={selectedMonth}
            treatments={groupedTreatments}
            openDialog={openPrintDialog}
            setOpenDialog={setOpenPrintDialog}
          />
        )}
      </Box>
    </>
  );
};

export default CreditedTreatmentsList;
